import React, { useState } from "react";
import { formatSgDateTime } from "../../lib/format";
import {
  Sprout,
  MessageCircle,
  Trash2,
  Pencil,
  X as XIcon,
  Save,
  EyeOff,
} from "lucide-react";

const MAX_POST = 2000;

const topicLabel = (slug, topics) => {
  const t = topics.find((x) => x.slug === slug);
  return t ? t.label : slug;
};

const PostCard = ({
  post,
  topics,
  isMine,
  isModerator,
  onReact,
  onOpenReplies,
  onDelete,
  onModeratorHide,
  onSaveEdit,
  busy,
}) => {
  const [editing, setEditing] = useState(false);
  const [draftTopic, setDraftTopic] = useState(post?.topic || "open");
  const [draftContent, setDraftContent] = useState(post?.content || "");
  const [saving, setSaving] = useState(false);

  const heardCount = post?.reactions?.heard || 0;
  const iReacted = (post?.my_reactions || []).includes("heard");

  const startEdit = () => {
    setDraftTopic(post?.topic || "open");
    setDraftContent(post?.content || "");
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
  };

  const handleSave = async () => {
    const trimmed = draftContent.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await onSaveEdit?.(post.id, {
        content: trimmed,
        topic: draftTopic,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="ht-card rounded-2xl bg-white border border-[#e6eef5] p-5 sm:p-6 shadow-sm">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#eaf4fb] text-[#3d6486] font-semibold ht-display shrink-0">
            {(post?.author?.first_name || "?").slice(0, 1).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="ht-body font-semibold text-[#2c3e50] truncate">
              {post?.author?.first_name || "Friend"}
            </p>
            <p className="text-xs text-[#7e8a96]">
              {formatSgDateTime(post?.created_at)}
              {post?.edited_at && (
                <span
                  className="ml-2 italic text-[#9aa6b2]"
                  title={`Edited ${formatSgDateTime(post.edited_at)}`}
                >
                  · edited
                </span>
              )}
            </p>
          </div>
        </div>
        {!editing && (
          <span className="inline-block rounded-full px-2.5 py-0.5 text-xs font-medium bg-[#eaf4fb] text-[#3d6486] shrink-0">
            {topicLabel(post?.topic, topics)}
          </span>
        )}
        {editing && (
          <select
            value={draftTopic}
            onChange={(e) => setDraftTopic(e.target.value)}
            className="ht-input rounded-md border border-[#dbe3eb] bg-white px-3 py-1 text-xs text-[#2c3e50]"
          >
            {topics.map((t) => (
              <option key={t.slug} value={t.slug}>
                {t.label}
              </option>
            ))}
          </select>
        )}
      </header>

      {!editing && (
        <p className="mt-4 ht-body text-[#2c3e50] whitespace-pre-wrap leading-relaxed">
          {post?.content}
        </p>
      )}

      {editing && (
        <div className="mt-4">
          <textarea
            value={draftContent}
            onChange={(e) => setDraftContent(e.target.value.slice(0, MAX_POST))}
            rows={5}
            className="ht-input w-full rounded-md border border-[#d6dde4] bg-white px-3.5 py-3 text-[15px] placeholder:text-[#9aa6b2] resize-y"
          />
          <p className="mt-1 text-[11px] text-[#9aa6b2] text-right">
            {draftContent.length}/{MAX_POST}
          </p>
        </div>
      )}

      <footer className="mt-5 flex items-center gap-2 flex-wrap">
        {!editing && (
          <>
            <button
              onClick={onReact}
              disabled={busy}
              className={`ht-btn inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm border ${
                iReacted
                  ? "bg-[#eaf4fb] border-[#cfe7f8] text-[#2c3e50]"
                  : "bg-white border-[#e6eef5] text-[#5a6b7d] hover:border-[#cfe7f8] hover:text-[#2c3e50]"
              }`}
              aria-pressed={iReacted}
            >
              <Sprout className="h-4 w-4" />
              <span>Heard you</span>
              {heardCount > 0 && (
                <span className="ml-1 text-xs text-[#7e8a96]">{heardCount}</span>
              )}
            </button>
            <button
              onClick={onOpenReplies}
              className="ht-btn inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm border bg-white border-[#e6eef5] text-[#5a6b7d] hover:border-[#cfe7f8] hover:text-[#2c3e50]"
            >
              <MessageCircle className="h-4 w-4" />
              <span>
                {post?.reply_count > 0
                  ? `${post.reply_count} ${post.reply_count === 1 ? "reply" : "replies"}`
                  : "Reply"}
              </span>
            </button>
            {isMine && (
              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={startEdit}
                  disabled={busy}
                  className="ht-btn inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-[#5a6b7d] hover:text-[#3d6486] border border-transparent hover:border-[#cfe7f8]"
                  aria-label="Edit my post"
                >
                  <Pencil className="h-4 w-4" />
                  <span className="hidden sm:inline">Edit</span>
                </button>
                <button
                  onClick={onDelete}
                  disabled={busy}
                  className="ht-btn inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-[#9aa6b2] hover:text-[#b04040]"
                  aria-label="Delete my post"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )}
            {!isMine && isModerator && (
              <button
                onClick={onModeratorHide}
                disabled={busy}
                className="ml-auto ht-btn inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm border border-[#f3d7d7] text-[#b04040] hover:bg-[#fdf6f6]"
                aria-label="Hide this post (moderator)"
                title="Moderator: hide this post from the public feed. Reversible from /admin."
              >
                <EyeOff className="h-4 w-4" />
                <span className="hidden sm:inline">Hide (mod)</span>
              </button>
            )}
          </>
        )}

        {editing && (
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="ht-btn inline-flex items-center gap-1.5 rounded-md bg-white border border-[#dbe3eb] hover:border-[#cfe7f8] text-[#5a6b7d] px-3.5 py-2 text-sm font-semibold"
            >
              <XIcon className="h-4 w-4" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !draftContent.trim()}
              className="ht-btn inline-flex items-center gap-1.5 rounded-md bg-[#3d6486] hover:bg-[#345674] text-white font-semibold px-3.5 py-2 text-sm disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </footer>
    </article>
  );
};

export default PostCard;
